import React from "react";
import { DateFormat, formatCurrency, formatDate } from "../../../../../utils";
import {
  SectionCard,
  SectionHeader,
  TitleBlock,
  SectionTitle,
  SubtitleText,
} from "../../styles";
import Button, { ButtonTypes } from "../../../../../components/Button";
import { CardFooterLink } from "../../../../../components/CardFooterLink";
import {
  HeaderActions,
  ManageLink,
  NetAmount,
  NetUnit,
  RecurringGrid,
  RecurringCard,
  Avatar,
  CardBody,
  CardTitle,
  CardMeta,
  FrequencyBadge,
  MetaDot,
  NextDateText,
  CardAmount,
  EmptyWrapper,
  SkeletonCard,
} from "./styles";
import { RecurringTransactionsSectionProps } from "./types";
import {
  computeMonthlySummary,
  getAmountSign,
  getAmountVariant,
  getAvatarLetter,
  getCardTitle,
} from "./utils";

export const RecurringTransactionsSection: React.FC<
  RecurringTransactionsSectionProps
> = ({ recurringTransactions, isLoading, onManage }) => {
  const summary = computeMonthlySummary(recurringTransactions);
  const netSign = getAmountSign(summary.variant);
  const netFormatted = `${netSign}${formatCurrency(Math.abs(summary.net))}`;

  const renderSkeleton = (
    <RecurringGrid>
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </RecurringGrid>
  );

  const renderEmpty = (
    <EmptyWrapper>No recurring transactions for this account.</EmptyWrapper>
  );

  const renderCards = (
    <RecurringGrid>
      {recurringTransactions.map((tx) => {
        const variant = getAmountVariant(tx.transactionType);
        const sign = getAmountSign(variant);
        const formattedAmount = `${sign}${formatCurrency(tx.amount)}`;
        return (
          <RecurringCard key={tx.id}>
            <Avatar $variant={variant}>{getAvatarLetter(tx)}</Avatar>
            <CardBody>
              <CardTitle>{getCardTitle(tx)}</CardTitle>
              <CardMeta>
                <FrequencyBadge>{tx.frequency}</FrequencyBadge>
                <MetaDot>·</MetaDot>
                <NextDateText>
                  Next {formatDate(tx.nextExecutionDate, DateFormat.DMY_SPACE)}
                </NextDateText>
              </CardMeta>
            </CardBody>
            <CardAmount $variant={variant}>{formattedAmount}</CardAmount>
          </RecurringCard>
        );
      })}
    </RecurringGrid>
  );

  const hasItems = recurringTransactions.length > 0;

  const renderContent = () => {
    if (isLoading && !hasItems) {
      return renderSkeleton;
    }
    if (!hasItems) {
      return renderEmpty;
    }
    return renderCards;
  };

  return (
    <SectionCard>
      <SectionHeader>
        <TitleBlock>
          <SectionTitle>Recurring transactions</SectionTitle>
          <SubtitleText>
            {summary.activeCount} active ·{" "}
            <NetUnit>
              net{" "}
              <NetAmount $variant={summary.variant}>{netFormatted}</NetAmount> /
              month
            </NetUnit>
          </SubtitleText>
        </TitleBlock>
        {hasItems && (
          <HeaderActions>
            <ManageLink
              type={ButtonTypes.link}
              onClick={onManage}
              aria-label="Manage recurring transactions"
            >
              Manage →
            </ManageLink>
          </HeaderActions>
        )}
      </SectionHeader>

      {renderContent()}

      {hasItems && (
        <CardFooterLink
          isMobileOnly
          onClick={onManage}
          aria-label="Manage recurring transactions"
        >
          Manage →
        </CardFooterLink>
      )}
    </SectionCard>
  );
};
